import type {
  TaskBaseFragment,
  TaskSummaryBaseFragment,
} from 'src/services/apollo/generated/graphql'
import { useModelExperimentSession } from 'src/services/orpc/modelExperiment'

export type EffectiveTaskStatus =
  | 'waiting'
  | 'running'
  | 'completed'
  | 'cancelled'
  | 'failed'
  | 'awaiting-choice'

/**
 * Both Task and TaskSummary expose `enrolledExperiment` as a nullable
 * sentinel enum (server PRs #11182 / #11291). Non-null means this is the
 * root of an A/B model experiment session.
 */
export function isExperimentRootInput(input: {
  task?: Pick<TaskBaseFragment, 'enrolledExperiment'> | null
  taskSummary?: Pick<TaskSummaryBaseFragment, 'enrolledExperiment'> | null
}): boolean {
  return (
    input.task?.enrolledExperiment != null ||
    input.taskSummary?.enrolledExperiment != null
  )
}

/**
 * Effective status maps backend's raw status + experiment session state to a
 * single value the UI cares about. Decoupled from raw task.status because
 * backend keeps root in 'running' until the user submits (PR 11181); using
 * session.status is more reliable for the "awaiting-choice" decision.
 *
 * IMPORTANT: only `session.status === 'selecting'` maps to awaiting-choice.
 * `'pending'` means the session row exists but at least one sub-task is
 * still generating — the UI should keep showing the regular Loading
 * placeholder during that window, not the "Choose your favorite" CTA.
 * See `ModelExperimentSessionStatus` in
 * `packages/pixai-server/src/constants/generationTask.ts`:
 *   pending   — root + subs created, no sub has terminated yet
 *   selecting — both subs completed; user picks via modal
 */
export function getEffectiveTaskStatus(
  rawStatus: string | undefined,
  isExperimentRoot: boolean,
  session: { status: string } | null | undefined,
): EffectiveTaskStatus {
  if (isExperimentRoot && session && session.status === 'selecting') {
    return 'awaiting-choice'
  }
  return (rawStatus ?? 'waiting') as EffectiveTaskStatus
}

type TaskOutputs = { mediaId?: string | null } | null | undefined

export function useTaskDisplay(input: {
  task?: (TaskBaseFragment & { outputs?: TaskOutputs }) | null
  taskSummary?: TaskSummaryBaseFragment | null
}) {
  const taskId = input.task?.id ?? input.taskSummary?.id
  const isExperimentRoot = isExperimentRootInput(input)

  const { data: session } = useModelExperimentSession({
    taskId,
    enabled: !!taskId && isExperimentRoot,
  })

  const rawStatus = input.task?.status ?? input.taskSummary?.status
  const effectiveStatus = getEffectiveTaskStatus(
    rawStatus ?? undefined,
    isExperimentRoot,
    session,
  )
  const isAwaitingChoice = effectiveStatus === 'awaiting-choice'

  // Suppress mediaId in awaiting-choice to force fallback rendering — keeps
  // the user blinded from any control-side image before they pick.
  const rawMediaId =
    (input.task?.outputs as TaskOutputs)?.mediaId ??
    input.taskSummary?.mediaId ??
    undefined
  const mediaId = isAwaitingChoice ? undefined : (rawMediaId ?? undefined)

  return {
    effectiveStatus,
    mediaId,
    session,
    isAwaitingChoice,
  }
}
